import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { LOGO_URL } from "@/lib/constants";
import { ArrowRight } from "lucide-react";

const SECTIONS = [
    {
        title: "Mission",
        to: "/about/mission",
        copy: "Why BENNE exists, who we're for, and the change we're trying to make in food media.",
    },
    {
        title: "Privacy",
        to: "/about/privacy",
        copy: "How we handle your data, what we store, and the third-party services we use.",
    },
    {
        title: "Terms & Conditions",
        to: "/about/terms",
        copy: "The legal stuff — usage, content rights, and submission terms.",
    },
];

export default function AboutPage() {
    return (
        <div data-testid="about-page">
            <PageHero
                eyebrow="About BENNE"
                title="A platform for the people remaking the table."
                lead="BENNE is an independent press release and promotion platform amplifying chefs, makers, farmers and operators whose stories have been historically under-told. We publish openings, awards, profiles and events — and the people, projects and questions that shape them."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "About" },
                ]}
            />
            <div className="px-4 md:px-8 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-7 space-y-6 font-sofia text-lg text-[#1B1B1B]/85 leading-[1.75]">
                    <p>
                        BENNE was founded by a small editorial collective with
                        roots in restaurant kitchens, farmer's markets, hotel
                        floors and food-writing rooms. We share one belief: the
                        future of food & hospitality is being written by people
                        who haven't always had the loudest microphones — and
                        their press deserves a serious, beautifully made home.
                    </p>
                    <p>
                        We distribute press releases, opening announcements,
                        awards news, leadership moves and event promotions. We
                        also run an Events Calendar, a public submissions
                        process, and a paid Concierge service that keeps the
                        lights on without compromising editorial independence.
                    </p>
                    <p>
                        We're funded by a mix of reader subscriptions, paid
                        submissions, and select partnerships. We do not accept
                        sponsored content disguised as editorial.
                    </p>
                </div>
                <aside className="lg:col-span-5">
                    <div className="bg-[#1B1B1B] text-white p-7 mb-6 flex items-center gap-4">
                        <img
                            src={LOGO_URL}
                            alt="BENNE"
                            className="h-20 w-20 object-contain"
                        />
                        <div>
                            <p className="font-poppins text-[#F5C53D] text-xs tracking-[0.2em] uppercase">
                                Independent
                            </p>
                            <p className="font-poppins font-black text-3xl tracking-tighter">
                                BENNE
                            </p>
                            <p className="font-sofia text-white/70 text-sm">
                                Est. 2024 · Brooklyn, NY
                            </p>
                        </div>
                    </div>
                    <ul className="space-y-3">
                        {SECTIONS.map((s) => (
                            <li key={s.to}>
                                <Link
                                    to={s.to}
                                    className="group flex items-start gap-4 p-5 border border-black/10 hover:border-[#E63B26] hover:bg-[#FDF9EF] transition"
                                    data-testid={`about-link-${s.to.split("/").pop()}`}
                                >
                                    <ArrowRight className="h-5 w-5 text-[#E63B26] mt-1 group-hover:translate-x-1 transition" />
                                    <div>
                                        <p className="font-poppins font-semibold text-lg">
                                            {s.title}
                                        </p>
                                        <p className="font-sofia text-sm text-[#1B1B1B]/70">
                                            {s.copy}
                                        </p>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </aside>
            </div>
        </div>
    );
}
